/**
 * AssignmentSection - "Assignment" form section molecule.
 *
 * Renders the section that owns who-is-assigned + when-it's-scheduled for
 * both work orders and inspections. Composes:
 *	 - An optional "Inspector" read-only display (current user, inspection only)
 *	 - The shared AssigneeFieldset (primary + additional assignees)
 *	 - Priority dropdown (low / medium / high / urgent)
 *	 - Date input — labelled "Scheduled Date" for work orders and
 *	   "Inspection Date" for inspections
 *	 - Optional Start / End time inputs with a Timezone override
 *
 * The unification matches the user's request to give Inspections the
 * same Assignment experience as Work Orders. The shared inspections
 * table now carries priority + start_time + end_time + the
 * inspection_assignees junction table to back this UI.
 *
 * Two hosts render it. The create/edit form (JobForm) stacks it between
 * the other section cards, so by default it draws the SectionCard chrome
 * with an "Assignment" heading. The assign-from-the-list dialog
 * (AssignWorkOrderModal) already supplies a title and a white panel, and
 * a card inside a card would spend 48px of a 320px phone on borders — so
 * it passes `frame="none"` and gets the fields alone. Everything the
 * fields do is identical between the two.
 */
import React from 'react';
import { SectionCard } from '../SectionCard';
import { AssigneeFieldset } from '../AssigneeFieldset';
import { TmAssigneeHoursFieldset } from '../TmAssigneeHoursFieldset';
import { RequireHoursFieldset } from '../RequireHoursFieldset';
import { WORK_ORDER_PRIORITY_OPTIONS, SCHEDULE_TIMEZONE_OPTIONS } from '../../../utils/constants';
import type { QuickAddOptions } from '../../../utils/quickAddUser';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Display info for the read-only Inspector field (inspection only). */
export interface AssignmentSectionInspector {
	firstName: string;
	lastName: string;
	email: string;
}

/** Priority option shape — value is the canonical enum stored on the record. */
export interface AssignmentPriorityOption {
	value: string;
	label: string;
}

/** Timezone option shape — value is an IANA timezone identifier. */
export interface AssignmentTimezoneOption {
	value: string;
	label: string;
}

/** How the section is framed — see the note in the file header. */
export type AssignmentSectionFrame = 'card' | 'none';

export interface AssignmentSectionProps {
	/** 'work_order' or 'inspection' — drives label copy + Inspector readout visibility. */
	formType: 'work_order' | 'inspection';
	/** Inspector to display read-only (inspection only). */
	inspector?: AssignmentSectionInspector | null;

	/** Primary assignee (technician) ID. */
	assigneeId: string;
	onAssigneeChange: (id: string) => void;
	/** Additional assignee IDs alongside the primary one. */
	additionalAssigneeIds: string[];
	onAdditionalAssigneesChange: (ids: string[]) => void;

	/** Priority enum value. */
	priority: string;
	onPriorityChange: (priority: string) => void;
	/** Priority options to render in the dropdown. Defaults to the shared
	 *  WORK_ORDER_PRIORITY_OPTIONS list. */
	priorityOptions?: readonly AssignmentPriorityOption[];

	/** Date — YYYY-MM-DD; semantics depend on formType. */
	scheduledDate: string;
	onScheduledDateChange: (date: string) => void;

	/** Optional local start time (HH:MM). Empty = unspecified. */
	startTime: string;
	onStartTimeChange: (time: string) => void;
	/** Optional local end time (HH:MM). Empty = unspecified. */
	endTime: string;
	onEndTimeChange: (time: string) => void;
	/** IANA timezone identifier used to convert start/end to UTC at submit. */
	timezone: string;
	onTimezoneChange: (tz: string) => void;
	/** Timezone options to render in the dropdown. Defaults to the shared
	 *  SCHEDULE_TIMEZONE_OPTIONS list. */
	timezoneOptions?: readonly AssignmentTimezoneOption[];

	/** When true, replace the standard assignee pickers with the
	 *  editable-rows variant so each assignee can record hours. Driven by
	 *  the "Require Hours" flag below — which the T&M sub-type forces on. */
	tmMode?: boolean;
	/** Per-assignee hours map keyed by user id (string form). */
	assigneeHours?: Record<string, string>;
	onAssigneeHoursChange?: (hours: Record<string, string>) => void;
	/** The work order's bucket of hours, when entered. Passed through to
	 *  the hours editor for its "x / y hrs" readout. */
	hoursBucketValue?: number;
	/** Whether this work order is T&M, which changes the hours editor's
	 *  heading and makes the bucket mandatory. */
	isTimeAndMaterials?: boolean;
	/** Offer "+ Quick Add User" on the assignee pickers (both the plain
	 *  pickers and the hours editor). `companyId` is the record's business. */
	quickAdd?: QuickAddOptions;
	/** Show who the job is assigned to without letting it change — for
	 *  anyone without the Can Reassign Work Orders permission. Handed to
	 *  whichever picker is showing. */
	primaryLocked?: boolean;

	/** "Require Hours" opt-in + its bucket. Work orders only — omit the
	 *  bundle entirely on inspections, which have no hour tracking. The
	 *  bundle shape mirrors RequireHoursFieldsetProps minus the styling
	 *  tokens, which this section already carries. */
	requireHours?: {
		checked: boolean;
		onCheckedChange: (checked: boolean) => void;
		locked?: boolean;
		lockedNote?: string;
		bucketHours: string;
		onBucketHoursChange: (value: string) => void;
		showBucket?: boolean;
		bucketRequired?: boolean;
		bucketElsewhereNote?: string;
	};

	/** Inline validation errors keyed by field name. Read here:
	 *  scheduledDate / inspectionDate, startTime, endTime; the assignee
	 *  and hours keys pass through to the fieldsets. */
	errors?: Record<string, string>;
	/** Standard styling tokens. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	errorBorder: string;
	/** Card chrome with an "Assignment" heading (the default), or the bare
	 *  fields for a host that already frames them, such as a dialog. */
	frame?: AssignmentSectionFrame;
	/** Optional extra classes appended to the section root. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const AssignmentSection: React.FC<AssignmentSectionProps> = ({
	formType,
	inspector,
	assigneeId,
	onAssigneeChange,
	additionalAssigneeIds,
	onAdditionalAssigneesChange,
	priority,
	onPriorityChange,
	priorityOptions = WORK_ORDER_PRIORITY_OPTIONS,
	scheduledDate,
	onScheduledDateChange,
	startTime,
	onStartTimeChange,
	endTime,
	onEndTimeChange,
	timezone,
	onTimezoneChange,
	timezoneOptions = SCHEDULE_TIMEZONE_OPTIONS,
	tmMode = false,
	assigneeHours = {},
	onAssigneeHoursChange,
	hoursBucketValue,
	isTimeAndMaterials = false,
	quickAdd,
	primaryLocked = false,
	requireHours,
	errors,
	labelClass,
	inputClass,
	normalBorder,
	errorBorder,
	frame = 'card',
	className = '',
}) => {
	/* The date label changes with the form type — "Scheduled Date" for
	   work orders, "Inspection Date" for inspections. The underlying
	   field is a date input either way. */
	const dateLabel = formType === 'work_order' ? 'Scheduled Date' : 'Inspection Date';
	const dateRequired = formType === 'inspection';
	const dateErrorKey = formType === 'inspection' ? 'inspectionDate' : 'scheduledDate';
	const dateError = errors?.[dateErrorKey];
	const startTimeError = errors?.startTime;
	const endTimeError = errors?.endTime;

	const fields = (
		<>
			{/* Inspector read-out — inspection forms only. The auth user
			    is automatically recorded as the inspector on the record;
			    this field communicates that fact in the UI. */}
			{formType === 'inspection' && (
				<div className="assignment-section__inspector mb-16px">
					<label className={labelClass}>Inspector</label>
					<div className="assignment-section__inspector-value px-12px py-10px bg-gray-50 border border-gray-200 rounded-md text-14px text-brand-text min-h-44px flex items-center">
						{inspector ? `${inspector.firstName} ${inspector.lastName} (${inspector.email})` : 'Current user'}
					</div>
					<p className="mt-4px text-12px font-sans text-brand-text-muted">Automatically set to the logged-in user.</p>
				</div>
			)}

			{/* "Require Hours" — the switch that decides which assignee UI
			    renders below. Placed directly above it because ticking the
			    box visibly rewrites what follows; putting it in a distant
			    section would make that look like a glitch. */}
			{requireHours && (
				<RequireHoursFieldset
					checked={requireHours.checked}
					onCheckedChange={requireHours.onCheckedChange}
					locked={requireHours.locked}
					lockedNote={requireHours.lockedNote}
					bucketHours={requireHours.bucketHours}
					onBucketHoursChange={requireHours.onBucketHoursChange}
					showBucket={requireHours.showBucket}
					bucketRequired={requireHours.bucketRequired}
					bucketElsewhereNote={requireHours.bucketElsewhereNote}
					errors={errors}
					labelClass={labelClass}
					inputClass={inputClass}
					normalBorder={normalBorder}
					errorBorder={errorBorder}
				/>
			)}

			{/* Primary + additional assignees. On an hours-tracked work
			    order the standard pill picker is swapped for an
			    editable-rows variant that captures hours per assignee. The
			    hours map and its setter must both be supplied for the swap
			    to engage — without them the form falls back to the standard
			    fieldset. Neither variant is scoped to the record's company:
			    that company is the customer, while assignees are our own
			    staff. */}
			{tmMode && onAssigneeHoursChange ? (
				<TmAssigneeHoursFieldset
					assigneeId={assigneeId}
					onAssigneeChange={onAssigneeChange}
					additionalAssigneeIds={additionalAssigneeIds}
					onAdditionalAssigneesChange={onAdditionalAssigneesChange}
					assigneeHours={assigneeHours}
					onAssigneeHoursChange={onAssigneeHoursChange}
					bucketHours={hoursBucketValue}
					isTimeAndMaterials={isTimeAndMaterials}
					errors={errors}
					quickAdd={quickAdd}
					primaryLocked={primaryLocked}
					labelClass={labelClass}
				/>
			) : (
				<AssigneeFieldset
					formType={formType}
					assigneeId={assigneeId}
					onAssigneeChange={onAssigneeChange}
					additionalAssigneeIds={additionalAssigneeIds}
					onAdditionalAssigneesChange={onAdditionalAssigneesChange}
					errors={errors}
					quickAdd={quickAdd}
					primaryLocked={primaryLocked}
					labelClass={labelClass}
				/>
			)}

			{/* Priority + Date — same row layout for both form types. */}
			<div className="assignment-section__schedule-row grid grid-cols-1 tablet:grid-cols-2 gap-16px mt-16px">
				<div className="assignment-section__field">
					<label htmlFor="priority" className={labelClass}>Priority</label>
					<select id="priority" value={priority} onChange={(e) => onPriorityChange(e.target.value)} className={`${inputClass} bg-white ${normalBorder}`}>
						{priorityOptions.map((p) => (
							<option key={p.value} value={p.value}>{p.label}</option>
						))}
					</select>
				</div>
				<div className="assignment-section__field">
					<label htmlFor="scheduledDate" className={labelClass}>
						{dateLabel}{dateRequired && <span className="text-red-500"> *</span>}
					</label>
					<input id="scheduledDate" type="date" value={scheduledDate} onChange={(e) => onScheduledDateChange(e.target.value)} className={`${inputClass} bg-white ${dateError ? errorBorder : normalBorder}`} />
					{dateError && <p className="mt-4px text-14px text-red-600">{dateError}</p>}
				</div>
			</div>

			{/* Optional start/end time + timezone override row. */}
			<div className="assignment-section__time-row grid grid-cols-1 tablet:grid-cols-3 gap-16px mt-16px">
				<div className="assignment-section__field">
					<label htmlFor="startTime" className={labelClass}>Start Time <span className="text-12px text-brand-text-muted font-normal">(optional)</span></label>
					<input id="startTime" type="time" value={startTime} onChange={(e) => onStartTimeChange(e.target.value)} className={`${inputClass} bg-white ${startTimeError ? errorBorder : normalBorder}`} />
					{startTimeError && <p className="mt-4px text-14px text-red-600">{startTimeError}</p>}
				</div>
				<div className="assignment-section__field">
					<label htmlFor="endTime" className={labelClass}>End Time <span className="text-12px text-brand-text-muted font-normal">(optional)</span></label>
					<input id="endTime" type="time" value={endTime} onChange={(e) => onEndTimeChange(e.target.value)} className={`${inputClass} bg-white ${endTimeError ? errorBorder : normalBorder}`} />
					{endTimeError && <p className="mt-4px text-14px text-red-600">{endTimeError}</p>}
				</div>
				<div className="assignment-section__field">
					<label htmlFor="scheduleTimezone" className={labelClass}>Timezone</label>
					<select id="scheduleTimezone" value={timezone} onChange={(e) => onTimezoneChange(e.target.value)} className={`${inputClass} bg-white ${normalBorder}`}>
						{timezoneOptions.map((tz) => (
							<option key={tz.value} value={tz.value}>{tz.label}</option>
						))}
					</select>
					<p className="mt-4px text-12px font-sans text-brand-text-muted">Defaults to your profile timezone. Times are stored in UTC.</p>
				</div>
			</div>
		</>
	);

	/* A host that already frames the fields (the assign dialog) gets them
	   bare, under the same block class and marker so tests and the DOM
	   read the same either way. */
	if (frame === 'none') {
		return (
			<div className={`assignment-section assignment-section--frameless ${className}`} data-ref="molecule-assignment-section">
				{fields}
			</div>
		);
	}

	return (
		<SectionCard title="Assignment" className={`assignment-section ${className}`} dataRef="molecule-assignment-section">
			{fields}
		</SectionCard>
	);
};

export default AssignmentSection;
